import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Reply as ReplyIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';
import FeatureGate from './FeatureGate';

const contentEditorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
};

const getStatusColor = (status) => {
  const map = {
    draft: 'default',
    sent: 'info',
    delivered: 'primary',
    read: 'success',
    replied: 'success',
    pending: 'warning',
    completed: 'success',
  };
  return map[status] || 'default';
};

const getRecipientsText = (recipients) => {
  if (!recipients || recipients.length === 0) return 'No recipients';
  const toRecipients = recipients.filter((r) => r.recipient_type === 'to');
  const ccRecipients = recipients.filter((r) => r.recipient_type === 'cc');
  let text = toRecipients.map((r) => r.name || r.email).join(', ');
  if (ccRecipients.length > 0) {
    text += ` (CC: ${ccRecipients.map((r) => r.name || r.email).join(', ')})`;
  }
  return text;
};

const formatDate = (dateString) => {
  const d = new Date(dateString);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const getPreview = (content, maxLen = 60) => {
  if (!content) return '';
  const text = String(content).replace(/<[^>]*>/g, '').trim();
  return text.length > maxLen ? text.substring(0, maxLen) + '…' : text;
};

const EmailThreadPanel = ({ clientId, onCommunicationSelect, refreshTrigger, onComposeEmail }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftSnackbar, setAiDraftSnackbar] = useState({ open: false, message: '' });

  const fetchThreads = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/clients/${clientId}/email-threads/`);
      const list = res.data?.threads ?? [];
      setThreads(list);
      if (selectedThread && !list.find((t) => t.id === selectedThread.id)) {
        setSelectedThread(null);
      }
    } catch {
      setError('Failed to load email threads');
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [clientId, refreshTrigger]);

  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
    setReplyContent('');
  };

  const handleAiDraft = async () => {
    if (!selectedThread?.id) return;
    setAiDraftLoading(true);
    try {
      const res = await enhancedApi.draftEmailResponse(selectedThread.id);
      const draft = res.data?.draft || '';
      const html = (draft || '').includes('<') ? draft : `<p>${(draft || '').replace(/\n/g, '</p><p>')}</p>`;
      setReplyContent(html);
    } catch {
      setAiDraftSnackbar({ open: true, message: 'AI draft unavailable' });
    } finally {
      setAiDraftLoading(false);
    }
  };

  const handleReplyClick = () => {
    const comms = selectedThread?.communications ?? [];
    const latestComm = comms[comms.length - 1];
    if (onCommunicationSelect && latestComm) {
      onCommunicationSelect(latestComm, 'reply');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* Thread list - 30% */}
      <Box
        sx={{
          width: '30%',
          minWidth: 200,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        {threads.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <EmailIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No email threads
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {threads.map((thread) => (
              <ListItemButton
                key={thread.id}
                selected={selectedThread?.id === thread.id}
                onClick={() => handleSelectThread(thread)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                      {thread.subject || '(No subject)'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {getPreview(thread.communications?.[0]?.content || thread.subject, 45)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(thread.updated_at)}
                      </Typography>
                      <Chip label={thread.latest_status} size="small" color={getStatusColor(thread.latest_status)} sx={{ height: 18 }} />
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Thread content - 70% */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedThread ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Select a thread to view
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {selectedThread.subject || '(No subject)'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {(selectedThread.communications || []).map((comm) => (
                <Paper
                  key={comm.id}
                  variant="outlined"
                  sx={{ p: 2, mb: 2, borderRadius: 2 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {(comm.created_by_name || 'U')[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {comm.created_by_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          To: {getRecipientsText(comm.recipients)} — {formatDate(comm.created_at)}
                        </Typography>
                      </Box>
                      <Chip label={comm.status} size="small" color={getStatusColor(comm.status)} sx={{ ml: 1 }} />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: comm.content || '<p></p>' }}
                    sx={{ whiteSpace: 'pre-wrap', mt: 1 }}
                  />
                </Paper>
              ))}
            </Box>

            {/* Reply section */}
            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Reply
                </Typography>
                <FeatureGate feature="ai_features">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={aiDraftLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleAiDraft}
                    disabled={aiDraftLoading}
                    sx={{ textTransform: 'none' }}
                  >
                    {aiDraftLoading ? 'Drafting…' : 'AI Draft Reply'}
                  </Button>
                </FeatureGate>
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 1,
                  '& .ql-container': { minHeight: 120 },
                  '& .ql-editor': { minHeight: 120 },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={replyContent}
                  onChange={setReplyContent}
                  placeholder="Type your reply…"
                  modules={contentEditorModules}
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<ReplyIcon />}
                onClick={handleReplyClick}
                sx={{ textTransform: 'none' }}
              >
                Reply
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EmailThreadPanel;
